import cloneDeep from 'lodash/cloneDeep';

import { GQL_order_order, GQL_order_order_orderLines, GQL_order_order_supplierParty } from '@/queries/__generated__/order';
import { generateUID } from '@/utils/utils';
import { GQL_OrderLineBase, GQL_OrderStatusEnum, GQL_CompanyNameOptionEnum, GQL_RoundingTypeEnum, GQL_NorwegianVatSymbolEnum } from '@/queries/global';
import { GQL_orderNewData_currentUser_currentUnit, GQL_orderNewData_currentUser } from '@/queries/__generated__/orderNewData';
import { GQL_recurringOrderFragments } from '@/queries/__generated__/recurringOrderFragments';
import { getCorrectVatRate } from './Vat';
import dayjs from '@/utils/dayjs';
import { productPriceAt } from '@/app/Product';
import Decimal from '@/utils/decimal';
import { GQL_orderProductsSearch_products } from '@/queries/__generated__/orderProductsSearch';
import { GQL_createCustomer_createCustomer_customer } from '@/queries/__generated__/createCustomer';
import { GQL_orderCustomerSearch_customers } from '@/queries/__generated__/orderCustomerSearch';
import { GQL_activeNorwegianVatRates_activeNorwegianVatRates } from '@/queries/__client__/activeNorwegianVatRates';

export const newOrderLineObject: (() => GQL_order_order_orderLines) = () => ({
  __typename: 'OrderLine',
  id: '',
  $_UID: generateUID(),
  $_expanded: false,
  number: 1,
  note: null,
  productDescription: null,
  productDescriptionEnabled: false,
  orderedQuantity: new Decimal(1),
  quantity: new Decimal(1),
  unitOfMeasureCode: '',
  lineExtensionAmount: new Decimal(0),
  vatRateSymbol: GQL_NorwegianVatSymbolEnum.ZERO,
  taxPercent: new Decimal(0),
  taxExemptionReason: null,
  taxAmount: new Decimal(0),
  priceAmount: new Decimal(0),
  product: {
    __typename: 'Product',
    id: '',
    name: '',
    supplierUnit: null,
    description: null,
    number: '',
    vatRateSymbol: GQL_NorwegianVatSymbolEnum.ZERO,
    taxExemptionReason: null,
    productPrices: [],
    unitOfMeasureCode: '',
    car: null,
    project: null,
    department: null,
    createdBy: {
      __typename: 'User',
      id: '',
      name: null,
    },
    productGroup: null,
    suppliersProductNumber: '',
    productActivePeriods: [],
  },
  itemName: '',
  itemDescription: null,
  sellersItemIdentificationId: '',
  car: {
    __typename: 'Car',
    id: '',
    registrationNumber: '',
    name: '',
  },
  project: {
    __typename: 'Project',
    id: '',
    name: '',
    code: '',
  },
  department: {
    __typename: 'Department',
    id: '',
    name: '',
    code: '',
  },
  accountingCost: '',
  orderLineAllowanceCharge: null,
});

export const newOrderObject: (() => GQL_order_order) = () => ({
  __typename: 'Order',
  id: '',
  number: 0,
  cancelledAt: null,
  cancelledBy: null,
  lockedAt: null,
  lockedBy: null,
  savedAt: dayjs(),
  savedBy: null,
  isLocked: false,
  isCancelled: false,
  invoice: null,
  unit: {
    __typename: 'Unit',
    id: '',
    name: '',
    isCompany: true,
    unitSetting: {
      __typename: 'UnitSetting',
      id: null,
      unitInvoiceSetting: {
        __typename: 'UnitInvoiceSetting',
        id: null,
        exports: false,
        productGroups: false,
        paymentMethods: false,
        legalNotes: [],
        customNote: null,
        taxInclusiveAmountRounding: GQL_RoundingTypeEnum.DISABLED,
        orderSaveLockConfirmation: false,
        displaySettings: {
          __typename: 'UnitSettingDisplaySettings',
          invoiceLineTaxAmount: false,
          invoiceLineAllowanceCharge: false,
          invoiceLineOrderedQuantity: false,
          invoiceLineCar: false,
          invoiceLineProject: false,
          invoiceLineDepartment: false,
          invoiceAllowanceCharge: false,
          invoiceCar: false,
          invoiceProject: false,
          invoiceDepartment: false,
        },
      },
    },
  },
  customer: {
    __typename: 'Customer',
    id: '',
    unit: {
      __typename: 'Unit',
      id: 'null',
      name: '',
      address: '',
      zip: '',
      city: '',
      mobile: '',
      openedDate: '',
      closedDate: '',
      country: {
        id: '',
        name: '',
        code: '',
        __typename: 'Country',
      },
      vatRegulation: false,
      highestPriorityIdentity: {
        __typename: 'Identity',
        id: '',
        value: '',
        identificator: {
          __typename: 'Identificator',
          id: '',
          name: '',
        },
      },
      foretaksRegisteredSince: '',
    },
    phone: '',
    email: '',
    unofficialName: '',
    customerContacts: [],
    deliveryLocations: [],
    reference: '',
  },
  logo: {
    __typename: 'Attachment',
    id: null,
    signedBlobId: null,
    url: null,
  },
  date: dayjs().format('YYYY-MM-DD'),
  currency: {
    __typename: 'Currency',
    id: '',
    code: '',
  },
  currencyCode: '',
  bban: '',
  iban: '',
  reference: null,
  taxAmount: new Decimal(0),
  lineExtensionAmount: new Decimal(0),
  taxExclusiveAmount: new Decimal(0),
  taxInclusiveAmount: new Decimal(0),
  payableAmount: new Decimal(0),
  note: '',
  status: GQL_OrderStatusEnum.OPEN,
  isOpen: true,
  export: false,
  car: {
    __typename: 'Car',
    id: '',
    registrationNumber: '',
    name: '',
  },
  project: {
    __typename: 'Project',
    id: '',
    code: '',
    name: '',
  },
  department: {
    __typename: 'Department',
    id: '',
    code: '',
    name: '',
  },
  accountingCost: '',
  supplierParty: {
    __typename: 'OrderParty',
    id: '',
    streetName: null,
    additionalStreetName: null,
    name: '',
    unofficialName: null,
    phone: null,
    mobile: null,
    email: null,
    partyIdentificationScheme: '',
    partyIdentification: '',
    city: '',
    zip: '',
    country: {
      __typename: 'Country',
      id: '',
      name: '',
      code: '',
    },
    countryCode: '',
    reference: null,
    vatRegulation: true,
    foretaksRegisteredSince: null,
  },
  customerParty: {
    __typename: 'OrderParty',
    id: '',
    streetName: null,
    additionalStreetName: null,
    name: '',
    unofficialName: null,
    phone: null,
    mobile: null,
    email: null,
    partyIdentificationScheme: '',
    partyIdentification: '',
    city: '',
    zip: '',
    country: {
      __typename: 'Country',
      id: '',
      name: '',
      code: '',
    },
    countryCode: '',
    reference: null,
    vatRegulation: true,
    foretaksRegisteredSince: null,
  },
  orderDelivery: {
    __typename: 'OrderDelivery',
    id: '',
    date: dayjs().format('YYYY-MM-DD'),
    streetName: '',
    additionalStreetName: null,
    city: '',
    zip: '',
    country: {
      __typename: 'Country',
      id: '',
      name: '',
      code: '',
    },
    countryCode: '',
  },
  orderLines: [],
  orderAllowanceCharges: [
    {
      __typename: 'OrderAllowanceCharge',
      id: '',
      reason: '',
      percent: new Decimal(0),
      orderAllowanceChargeLines: [
      ],
    },
    {
      __typename: 'OrderAllowanceCharge',
      id: '',
      reason: '',
      percent: new Decimal(0),
      orderAllowanceChargeLines: [
      ],
    },
  ],
});

// TODO: Use GQL_createOrderVariables as return type.
export const formDataToMutationInput = (data: { order: GQL_order_order }) => {
  return {
    ...data.order,
    unit: undefined,
    unitId: data.order.unit.id,
    customer: undefined,
    customerId: data.order.customer.id,
    car: undefined,
    carId: data.order.car?.id || null,
    project: undefined,
    projectId: data.order.project?.id || null,
    department: undefined,
    departmentId: data.order.department?.id || null,
    accountingCost: undefined,
    supplierParty: {
      ...data.order.supplierParty,
      country: undefined,
    },
    customerParty: {
      ...data.order.customerParty,
      country: undefined,
    },
    orderDelivery: {
      ...data.order.orderDelivery,
      country: undefined,
    },
    orderLines: data.order.orderLines.map(line => {
      return {
        ...line,
        $_UID: undefined,
        $_page: undefined,
        $_expanded: undefined,
        product: undefined,
        productId: line.product?.id || null,
        car: undefined,
        carId: line.car?.id || null,
        project: undefined,
        projectId: line.project?.id || null,
        department: undefined,
        departmentId: line.department?.id || null,
        accountingCost: undefined,
      };
    }),
    orderAllowanceCharges: data.order.orderAllowanceCharges.map(ac => {
      return {
        ...ac,
        orderAllowanceChargeLines: ac.orderAllowanceChargeLines.map(acl => {
          return {
            ...acl,
          };
        }),
      };
    }),
  };
};

export const orderHasQuantityDifference = (order: GQL_order_order): boolean => {
  return order.orderLines.some(line => !(line as GQL_OrderLineBase)._destroy && line.orderedQuantity.abs().greaterThan(line.quantity.abs()));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const populateOrderWithUserNonChangeableSupplierInfo = (order: GQL_order_order | GQL_recurringOrderFragments, supplier: GQL_orderNewData_currentUser_currentUnit) => {
  order.unit = supplier;
  order.logo = supplier.unitSetting.logo;
  order.supplierParty.name = supplier.name;
  order.supplierParty.unofficialName = supplier.unitSetting.companyNameOption === GQL_CompanyNameOptionEnum.ALIAS ? supplier.unofficialName : null;
  order.supplierParty.streetName = supplier.address;
  order.supplierParty.zip = supplier.zip ? supplier.zip : '';
  order.supplierParty.city = supplier.city ? supplier.city : '';
  order.supplierParty.country = supplier.country;
  order.supplierParty.countryCode = supplier.country.code;
  order.supplierParty.phone = supplier.phone;
  order.supplierParty.mobile = supplier.mobile;
  order.supplierParty.email = supplier.email;
  order.supplierParty.vatRegulation = supplier.vatRegulation;
  order.supplierParty.foretaksRegisteredSince = supplier.foretaksRegisteredSince;

  order.supplierParty.partyIdentificationScheme = supplier.highestPriorityIdentity.identificator.name;
  order.supplierParty.partyIdentification = supplier.highestPriorityIdentity.value;

  if (supplier.unitSetting.unitInvoiceSetting.bban) {
    order.bban = supplier.unitSetting.unitInvoiceSetting.bban || '';
  }

  if (supplier.unitSetting.unitInvoiceSetting.iban) {
    order.iban = supplier.unitSetting.unitInvoiceSetting.iban || '';
  }

};

export const populateOrderWithUserChangeableSupplierInfo = (order: GQL_order_order | GQL_recurringOrderFragments, supplier: GQL_orderNewData_currentUser_currentUnit) => {
  order.currencyCode = supplier.unitSetting.unitInvoiceSetting.currencyCode;
};

export const populateOurReference = (supplierParty: GQL_order_order_supplierParty, currentUser: GQL_orderNewData_currentUser) => {
  const reference = {
    FULL_NAME: currentUser.name,
    FIRST_NAME: currentUser.firstName,
    LAST_NAME: currentUser.lastName,
  }[currentUser.currentUnit.unitSetting.unitInvoiceSetting.supplierPartyReferenceSetting];
  supplierParty.reference = reference;
};

export const populateOrderWithUserNonChangeableCustomerInfo = (order: GQL_order_order | GQL_recurringOrderFragments, customer: GQL_orderCustomerSearch_customers | GQL_createCustomer_createCustomer_customer ) => {
  order.customer = { ...customer };
  order.customerParty.name = customer.unit.name;
  order.customerParty.unofficialName = customer.unofficialName;
  order.customerParty.streetName = customer.unit.address;
  order.customerParty.zip = customer.unit.zip || '';
  order.customerParty.city = customer.unit.city || '';
  order.customerParty.mobile = customer.unit.mobile;
  order.customerParty.phone = customer.phone;
  order.customerParty.email = customer.email;

  order.customerParty.partyIdentificationScheme = customer.unit.highestPriorityIdentity.identificator.name;
  order.customerParty.partyIdentification = customer.unit.highestPriorityIdentity.value;

  order.customerParty.country = customer.unit.country;
  order.customerParty.countryCode = customer.unit.country.code;

  order.customerParty.vatRegulation = customer.unit.vatRegulation;
  order.customerParty.foretaksRegisteredSince = customer.unit.foretaksRegisteredSince;
};

export const populateOrderWithUserChangeableCustomerInfo = (order: GQL_order_order | GQL_recurringOrderFragments, customer: GQL_orderCustomerSearch_customers | GQL_createCustomer_createCustomer_customer ) => {
  order.customer = { ...customer };
  order.customerParty.reference = customer.reference;

  order.orderDelivery.streetName = customer.deliveryLocations[0].address;
  order.orderDelivery.additionalStreetName = customer.deliveryLocations[0].additionalAddress;
  order.orderDelivery.zip = customer.deliveryLocations[0].zip;
  order.orderDelivery.city = customer.deliveryLocations[0].city;
  order.orderDelivery.country = customer.deliveryLocations[0].country;
  order.orderDelivery.countryCode = customer.deliveryLocations[0].country.code;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const modifyOrderData = (order: any) => {
  if (!order.car) order.car = { id: null };
  if (!order.project) order.project = { id: null };
  if (!order.department) order.department = { id: null };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order.orderLines = order.orderLines.map((line: any) => {
    if (!line.car) line.car = { id: null };
    if (!line.project) line.project = { id: null };
    if (!line.department) line.department = { id: null };
    if (!line.product) line.product = { id: null };
    if (!line.$_UID) line.$_UID = generateUID();
    if (!line.$_expanded) line.$_expanded = false;
    return line;
  });
  return cloneDeep(order);
};

export const setCorrectVatRateSymbolToLine = (
  line: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  product: GQL_orderProductsSearch_products,
  order: GQL_order_order,
  activeVatRates: GQL_activeNorwegianVatRates_activeNorwegianVatRates[],
) => {
  const vatRate = getCorrectVatRate(
    activeVatRates,
    product.vatRateSymbol,
    order.export,
    order.supplierParty.vatRegulation,
  );

  if (vatRate) {
    line.vatRateSymbol = vatRate.symbol;
    line.taxPercent = vatRate.rate;
    line.taxExemptionReason = product.taxExemptionReason;
  }
};

export const setProductToLine = (
  line: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  product: GQL_orderProductsSearch_products,
  order: GQL_order_order,
  activeVatRates: GQL_activeNorwegianVatRates_activeNorwegianVatRates[],
) => {
  line.product = product;
  line.itemName = product.name;
  line.productDescription = product.description;
  line.productDescriptionEnabled = true;
  line.priceAmount = productPriceAt(dayjs.strict(order.date), product)?.salePrice || new Decimal(0);
  line.sellersItemIdentificationId = product.number;
  line.unitOfMeasureCode = product.unitOfMeasureCode;

  setCorrectVatRateSymbolToLine(line, product, order, activeVatRates);

  if (!line.car.id) {
    line.car = product.car || { id: null };
  }

  if (!line.project.id) {
    line.project = product.project || { id: null };
  }

  if (!line.department.id) {
    line.department = product.department || { id: null };
  }
};

export interface IAccountingCostEntry {
  present: boolean;
  display: string;
}

export interface IAccountingCost {
  car: IAccountingCostEntry;
  project: IAccountingCostEntry;
  department: IAccountingCostEntry;
}

interface ILiveCode {
  id?: string | null;
  code?: string | null;
  name?: string | null;
}

interface ILiveCar {
  id?: string | null;
  registrationNumber?: string | null;
  name?: string | null;
}

// The order's or line's own car/project/department, as they are now.
export interface ILiveAccountingCost {
  car?: ILiveCar | null;
  project?: ILiveCode | null;
  department?: ILiveCode | null;
}

const liveEntry = (code?: string | null, name?: string | null, id?: string | null): IAccountingCostEntry => (
  id ? { present: true, display: `${code} - ${name}` } : { present: false, display: '-' }
);

// Reads the accounting cost snapshot of a locked order or line. A snapshot of
// null (not '') means none was ever taken: orders locked before the snapshot
// existed. Those fall back to the live values passed in, rather than hiding
// the car/project/department.
export const parseAccountingCost = (accountingCost?: string | null, live?: ILiveAccountingCost): IAccountingCost => {
  const absent = () => ({ present: false, display: '-' });
  const result: IAccountingCost = {
    car: absent(),
    project: absent(),
    department: absent(),
  };

  if (accountingCost === null || accountingCost === undefined) {
    if (!live) return result;

    return {
      car: liveEntry(live.car?.registrationNumber, live.car?.name, live.car?.id),
      project: liveEntry(live.project?.code, live.project?.name, live.project?.id),
      department: liveEntry(live.department?.code, live.department?.name, live.department?.id),
    };
  }

  if (!accountingCost) return result;

  const parts: Record<string, string> = {};
  for (const part of accountingCost.split(/[;]\s*/)) {
    const [ key, value ] = part.split('=');
    if (key && value) parts[key.trim()] = value.trim();
  }

  if (parts.carcode && parts.car) {
    result.car = { present: true, display: `${parts.carcode} - ${parts.car}` };
  }

  if (parts.projectcode && parts.projectname) {
    result.project = { present: true, display: `${parts.projectcode} - ${parts.projectname}` };
  }

  if (parts.departmentcode && parts.departmentname) {
    result.department = { present: true, display: `${parts.departmentcode} - ${parts.departmentname}` };
  }

  return result;
};
